// frontend/client/src/ProfilePage.js
import React, { useEffect, useRef, useState } from "react";
import "./ProfilePage.css";

/* Inline icons (no deps) */
function IconSettings(props) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" {...props}>
      <path
        d="M12 15.5a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7Z"
        strokeWidth="2"
      />
      <path
        d="M19.4 15a7.9 7.9 0 0 0 .1-1 7.9 7.9 0 0 0-.1-1l2-1.5-2-3.5-2.4 1a7.6 7.6 0 0 0-1.7-1l-.4-2.6h-4l-.4 2.6a7.6 7.6 0 0 0-1.7 1l-2.4-1-2 3.5 2 1.5a7.9 7.9 0 0 0-.1 1 7.9 7.9 0 0 0 .1 1l-2 1.5 2 3.5 2.4-1a7.6 7.6 0 0 0 1.7 1l.4 2.6h4l.4-2.6a7.6 7.6 0 0 0 1.7-1l2.4 1 2-3.5-2-1.5Z"
        strokeWidth="2"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function IconPencil(props) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" {...props}>
      <path d="M12 20h9" strokeWidth="2" strokeLinecap="round" />
      <path
        d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5Z"
        strokeWidth="2"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function VerifiedTick(props) {
  // Replit file'daki tik path'i birebir
  return (
    <svg viewBox="0 0 100 100" fill="none" {...props}>
      <path
        d="M 25 55 Q 30 40 35 55 L 45 75 L 75 35 Q 85 30 85 45"
        stroke="#10b981"
        strokeWidth="10"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function IconBack(props) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" {...props}>
      <path
        d="M15 18l-6-6 6-6"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

const REL_OPTIONS = [
  "Not set",
  "Single",
  "In a relationship",
  "It's complicated",
  "Prefer not to say",
  "Studying only",
  "Get summersaulted",
];

function initialsFromName(name) {
  const parts = String(name || "").trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "PP";
  const a = parts[0][0] || "";
  const b = parts.length > 1 ? (parts[1][0] || "") : (parts[0][1] || "");
  return (a + b).toUpperCase() || "PP";
}

export default function ProfilePage() {
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [posts, setPosts] = useState([]);
  const [error, setError] = useState(null);

  const [postCountTotal, setPostCountTotal] = useState(0);
  const [followerCount, setFollowerCount] = useState(0);
  const [followingCount, setFollowingCount] = useState(0);

  const [bio, setBio] = useState('"Software developer | Dreamer | Building the future 🚀"');
  const [isSaved, setIsSaved] = useState(false);
  const [relationshipStatus, setRelationshipStatus] = useState("It's complicated");

  const [csrfToken, setCsrfToken] = useState("");
  const [profileCompleted, setProfileCompleted] = useState(false);

  const [avatarFile, setAvatarFile] = useState(null);
  const fileInputRef = useRef(null);

  const [relOpen, setRelOpen] = useState(false);

  // Follow Requests (accordion)
  const [frOpen, setFrOpen] = useState(false);
  const [frLoading, setFrLoading] = useState(false);
  const [frError, setFrError] = useState("");
  const [incomingReqs, setIncomingReqs] = useState([]);
  const [outgoingReqs, setOutgoingReqs] = useState([]);

  const loadProfile = () => {
    setLoading(true);
    setError(null);

    fetch("/api/profile", { credentials: "include" })
      .then((res) => res.json().catch(() => ({})))
      .then((data) => {
        if (!data.ok) throw new Error(data.error || "profile_load_failed");

        const p = data.profile || {};
        setProfile(p);
        setPosts(Array.isArray(data.posts) ? data.posts : []);

        setFollowerCount(data.followerCount || 0);
        setFollowingCount(data.followingCount || 0);
        setPostCountTotal(data.postCountTotal || 0);
        setCsrfToken(data.csrfToken || "");
        const defaultBio = '"bio"';
        setRelationshipStatus(p.relationshipStatus || "Not set");
        setBio(p.bio && String(p.bio).trim() ? p.bio : defaultBio);
        setProfileCompleted(!!p.profileCompleted);

        setLoading(false);
      })
      .catch((err) => {
        console.error("profile load error", err);
        setError("Could not load profile");
        setLoading(false);
      });
  };

    const loadFollowRequests = () => {
      setFrLoading(true);
      setFrError("");

      fetch("/api/follow-requests", { credentials: "include" })
        .then((res) => res.json().catch(() => ({})))
        .then((data) => {
          if (!data.ok) throw new Error(data.error || "follow_requests_failed");
          setIncomingReqs(Array.isArray(data.incoming) ? data.incoming : []);
          setOutgoingReqs(Array.isArray(data.outgoing) ? data.outgoing : []);
          setFrLoading(false);
        })
        .catch((err) => {
          console.error("follow requests load error", err);
          setFrError("Could not load follow requests");
          setFrLoading(false);
        });
    };

    const postFrAction = (url, userId) => {
      setFrError("");
      return fetch(url, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId, csrfToken }),
      })
        .then((res) => res.json().catch(() => ({})))
        .then((data) => {
          if (!data.ok) throw new Error(data.error || "action_failed");
          // refresh lists + also refresh counts on profile
          loadFollowRequests();
          loadProfile();
        })
        .catch((err) => {
          console.error("follow request action error", err);
          setFrError("Action failed");
        });
    };

    const acceptRequest = (userId) => postFrAction("/api/follow-accept", userId);
    const rejectRequest = (userId) => postFrAction("/api/follow-reject", userId);
    const cancelRequest = (userId) => postFrAction("/api/follow-cancel", userId);

  useEffect(() => {
    loadProfile();
  }, []);

  const avatarSrc =
    profile?.profileImageUrl && String(profile.profileImageUrl).trim()
      ? profile.profileImageUrl
      : "/avatar";

  const nameText = profile?.fullName || "User";
  const uniText = profile?.university || "—";

  const onBioChange = (e) => {
    setBio(e.target.value);
    if (isSaved) setIsSaved(false);
  };

  const onPickAvatar = () => {
    if (fileInputRef.current) fileInputRef.current.click();
  };

  const onSave = (e) => {
    e.preventDefault();

    const formData = new FormData();
    // backend bunu istiyor: mevcut fullname’i geri yolla
    formData.append("fullName", profile?.fullName || "");
    formData.append("relationshipStatus", relationshipStatus || "");
    formData.append("bio", bio || "");
    if (avatarFile) formData.append("avatar", avatarFile);
    formData.append("csrfToken", csrfToken || "");

    fetch("/profile", {
      method: "POST",
      credentials: "include",
      redirect: "manual",
      body: formData,
    })
      .then(async (res) => {
        // Backend returns 302 on success, 4xx/5xx on error
        if (res.type === "opaqueredirect" || (res.status >= 300 && res.status < 400)) {
          // Success — backend redirected
          if (!profileCompleted) {
            window.location.href = "/welcome-wall?mode=first";
            return;
          }
          setIsSaved(true);
          setAvatarFile(null);
          if (fileInputRef.current) fileInputRef.current.value = "";
          loadProfile();
          return;
        }

        if (res.status >= 400) {
          const text = await res.text().catch(() => "");
          throw new Error(text || "save_failed");
        }
      })
      .catch((err) => {
        console.error("profile save error", err);
        setError("Could not save profile");
      });
  };

  if (loading) {
    return (
      <div className="rp-root">
        <div className="rp-shell">
          <div className="rp-loading">Loading profile...</div>
        </div>
      </div>
    );
  }

  if (error || !profile) {
    return (
      <div className="rp-root">
        <div className="rp-shell">
          <div className="rp-error">Error: {error || "Unknown error"}</div>
        </div>
      </div>
    );
  }

  // Replit grid 3 item. Eksikse placeholder.
  const grid = [posts[0] || null, posts[1] || null, posts[2] || null];

  return (
    <div className="rp-root">
      <div className="rp-shell">
        {/* Header Name & Actions */}
        <div className="rp-header">
          <div className="rp-header-left">
            <h1 className="rp-h1">{nameText}</h1>
            <div className="rp-tick-wrap">
              <VerifiedTick className="rp-tick" />
            </div>
          </div>

          <button
            className="rp-settings"
            type="button"
            title="Back"
            onClick={() => (window.location.href = "/react#/dashboard")}
          >
            <IconBack className="rp-settings-icon" />
          </button>
        </div>

        {/* Main Content Area */}
        <div className="rp-main">
          {/* Top Row: Avatar & Stats */}
          <div className="rp-toprow">
            {/* Avatar Frame (16-gon) */}
            <div className="rp-avatar-frame" role="button" tabIndex={0} onClick={onPickAvatar}>
              <div className="rp-avatar-outer" />
              <div className="rp-avatar-inner">
                <img
                  className="rp-avatar-img"
                  src={avatarSrc}
                  alt={nameText}
                  onError={(e) => {
                    e.currentTarget.style.display = "none";
                  }}
                />
                <div className="rp-avatar-fallback">{initialsFromName(nameText)}</div>
              </div>

              <input
                ref={fileInputRef}
                className="rp-file"
                type="file"
                accept="image/*"
                onChange={(e) => {
                  const file =
                    e.target.files && e.target.files.length > 0 ? e.target.files[0] : null;
                  setAvatarFile(file);
                  if (isSaved) setIsSaved(false);
                }}
              />
            </div>

            {/* Stats */}
            <div className="rp-stats-wrap">
              <div className="rp-stats">
                <div className="rp-stat">
                  <span className="rp-stat-label">posts</span>
                  <span className="rp-stat-value">{postCountTotal}</span>
                </div>
                <div className="rp-stat">
                  <span className="rp-stat-label">followers</span>
                  <span className="rp-stat-value">{followerCount}</span>
                </div>
                <div className="rp-stat">
                  <span className="rp-stat-label">following</span>
                  <span className="rp-stat-value">{followingCount}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Bottom Row: Profile Details & Bio */}
          <div className="rp-bottomrow">
            {/* Info Block */}
            <div className="rp-info">
              <div className="rp-info-block">
                <span className="rp-info-label">University:</span>
                <span className="rp-info-value">{uniText}</span>
              </div>

              <div className="rp-info-block rp-rel-group">
                <div className="rp-rel-head">
                  <span className="rp-info-label">Relationship Status:</span>

                  <button
                    type="button"
                    className="rp-rel-edit"
                    title="Edit"
                    onClick={() => setRelOpen((v) => !v)}
                  >
                    <IconPencil className="rp-pencil" />
                  </button>
                </div>

                <span className="rp-info-value">{relationshipStatus}</span>

                {relOpen && (
                  <div className="rp-rel-menu" onMouseLeave={() => setRelOpen(false)}>
                    {REL_OPTIONS.map((opt) => (
                      <button
                        key={opt}
                        type="button"
                        className={
                          "rp-rel-item" + (relationshipStatus === opt ? " rp-rel-item-active" : "")
                        }
                        onClick={() => {
                          setRelationshipStatus(opt);
                          setRelOpen(false);
                          if (isSaved) setIsSaved(false);
                        }}
                      >
                        {opt}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Bio Box & Action Button */}
            <form className="rp-bio-wrap" onSubmit={onSave}>
              <div className="rp-bio-box">
                <input
                  type="text"
                  value={bio}
                  onChange={onBioChange}
                  className="rp-bio-input"
                  placeholder="Biyografini yaz..."
                  maxLength={240}
                />
              </div>

              <button
                type="submit"
                className={"rp-save-btn" + (isSaved ? " rp-save-saved" : "")}
              >
                {isSaved ? "Saved" : "Save"}
              </button>
            </form>
          </div>
        </div>

                {/* Thin Divider */}
                <div className="rp-divider" />

                {/* Follow Requests Toggle + Panel (between info and posts) */}
                <button
                  type="button"
                  className={"rp-fr-toggle" + (frOpen ? " rp-fr-toggle-open" : "")}
                  onClick={() => {
                    setFrOpen((v) => {
                      const next = !v;
                      if (next) loadFollowRequests();
                      return next;
                    });
                  }}
                >
                  <span className="rp-fr-toggle-text">FOLLOW REQUESTS</span>
                  <span className="rp-fr-toggle-caret">{frOpen ? "▲" : "▼"}</span>
                </button>

                {frOpen && (
                  <div className="rp-fr-panel">
                    {frLoading && <div className="rp-fr-state">Loading…</div>}
                    {frError && <div className="rp-fr-state rp-fr-error">{frError}</div>}

                    {!frLoading && !frError && (
                      <div className="rp-fr-columns">
                        {/* Incoming */}
                        <div className="rp-fr-col">
                          <div className="rp-fr-col-title">
                            Requests you received ({incomingReqs.length})
                          </div>

                          {incomingReqs.length === 0 ? (
                            <div className="rp-fr-empty">No incoming requests.</div>
                          ) : (
                            incomingReqs.map((u) => (
                              <div key={`in-${u.id}`} className="rp-fr-row">
                                <img
                                  className="rp-fr-avatar"
                                  src={`/post-author-avatar?userId=${u.id}`}
                                  alt="Avatar"
                                  onError={(e) => {
                                    e.currentTarget.style.display = "none";
                                  }}
                                />
                                <div className="rp-fr-user">
                                  <div className="rp-fr-name">{u.fullName || "Unknown"}</div>
                                  <div className="rp-fr-meta">{u.university || ""}</div>
                                  <button
                                    type="button"
                                    className="rp-fr-link"
                                    onClick={() => {
                                      window.location.href = `/react#/user-profile?id=${encodeURIComponent(String(u.id))}&source=profile-requests`;
                                    }}
                                  >
                                    View profile
                                  </button>
                                </div>

                                <div className="rp-fr-actions">
                                  <button
                                    type="button"
                                    className="rp-fr-btn"
                                    onClick={() => acceptRequest(u.id)}
                                  >
                                    Accept
                                  </button>
                                  <button
                                    type="button"
                                    className="rp-fr-btn rp-fr-btn-ghost"
                                    onClick={() => rejectRequest(u.id)}
                                  >
                                    Reject
                                  </button>
                                </div>
                              </div>
                            ))
                          )}
                        </div>

                        {/* Outgoing */}
                        <div className="rp-fr-col">
                          <div className="rp-fr-col-title">
                            Requests you sent ({outgoingReqs.length})
                          </div>

                          {outgoingReqs.length === 0 ? (
                            <div className="rp-fr-empty">No sent requests.</div>
                          ) : (
                            outgoingReqs.map((u) => (
                              <div key={`out-${u.id}`} className="rp-fr-row">
                                <img
                                  className="rp-fr-avatar"
                                  src={`/post-author-avatar?userId=${u.id}`}
                                  alt="Avatar"
                                  onError={(e) => {
                                    e.currentTarget.style.display = "none";
                                  }}
                                />
                                <div className="rp-fr-user">
                                  <div className="rp-fr-name">{u.fullName || "Unknown"}</div>
                                  <div className="rp-fr-meta">{u.university || ""}</div>
                                  <button
                                    type="button"
                                    className="rp-fr-link"
                                    onClick={() => {
                                      window.location.href = `/react#/user-profile?id=${encodeURIComponent(String(u.id))}&source=profile-requests`;
                                    }}
                                  >
                                    View profile
                                  </button>
                                </div>

                                <div className="rp-fr-actions">
                                  <button
                                    type="button"
                                    className="rp-fr-btn rp-fr-btn-ghost"
                                    onClick={() => cancelRequest(u.id)}
                                  >
                                    Cancel
                                  </button>
                                </div>
                              </div>
                            ))
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Thin Divider */}
                <div className="rp-divider" />

                {/* Posts Grid */}
                <div className="rp-grid">
                  {grid.map((p, i) => {
                    if (!p) {
                      return (
                        <div key={`ph-${i}`} className="rp-tile">
                          <div className="rp-tile-bubble">
                            <span className="rp-tile-num">{i + 1}</span>
                          </div>
                        </div>
                      );
                    }

                    const href = `/react#/dashboard?scrollTo=post-${p.id}`;

                    if (p.hasImage) {
                      return (
                        <a key={p.id} href={href} className="rp-tile rp-tile-link" title="View post">
                          <img className="rp-tile-img" src={`/post-image?id=${p.id}`} alt="Post" loading="lazy" />
                        </a>
                      );
                    }

                    return (
                      <a key={p.id} href={href} className="rp-tile rp-tile-link" title="View post">
                        <div className="rp-tile-text">{p.previewText || ""}</div>
                      </a>
                    );
                  })}
                </div>
      </div>
    </div>
  );
}